import { GitBranch, Lock, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NodeIcon } from "@/components/NodeIcon";
import { NewNodeDialog } from "@/components/nodes/NewNodeDialog";
import type { HeadlineValueDto } from "@/api/types";
import { chanceLabel, setsText } from "@/components/shell/scenarioSummary";
import { useViewResult } from "@/components/shell/view";
import { useHome, useSearch } from "@/hooks/world";
import { fmtValue, pct } from "@/lib/format";
import { fmtByDate } from "@/lib/months";
import { headline, heroDate } from "@/lib/results";
import { cn } from "@/lib/utils";
import { useStore } from "@/model/store";
import type { ModelNode, MetricNode, Scenario } from "@/model/types";

type ScenarioFigures = { probability: number | null; hypothetical: boolean; impossible: boolean };
type Result =
	| { kind: "node"; node: ModelNode; value: HeadlineValueDto | null }
	| { kind: "scenario"; scenario: Scenario; figures: ScenarioFigures | null }
	| { kind: "create"; what: "event" | "metric" };

function useDebounced<T>(value: T, ms: number): T {
	const [v, setV] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setV(value), ms);
		return () => clearTimeout(t);
	}, [value, ms]);
	return v;
}

/**
 * The header search (⌘K or /): every event, metric and scenario, grouped, with
 * each node's headline number. Nothing found offers to create it.
 */
export function MegaSearch() {
	const nodes = useStore((s) => s.world.nodes);
	const scenarios = useStore((s) => s.world.scenarios);
	const view = useViewResult();
	const navigate = useNavigate();
	const inputRef = useRef<HTMLInputElement>(null);
	const boxRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const [q, setQ] = useState("");
	const [open, setOpen] = useState(false);
	const [active, setActive] = useState(0);
	const [creating, setCreating] = useState<{ kind: "event" | "metric"; title: string } | null>(null);

	// ⌘K / Ctrl-K or "/" focuses the search from anywhere.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			const el = e.target instanceof HTMLElement ? e.target : null;
			const typing = !!el && (el.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])') || el.isContentEditable);
			if (((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") || (e.key === "/" && !typing)) {
				e.preventDefault();
				inputRef.current?.focus();
				setOpen(true);
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	// Close when clicking outside.
	useEffect(() => {
		if (!open) return;
		const onDown = (e: PointerEvent) => {
			if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
		};
		window.addEventListener("pointerdown", onDown);
		return () => window.removeEventListener("pointerdown", onDown);
	}, [open]);

	const query = q.trim().toLowerCase();
	// Matching and ranking are the API's (`GET /search`); an empty box lists everything.
	const searched = useDebounced(q.trim(), 120);
	const search = useSearch(open ? searched : "", view.scenario?.id);
	const home = useHome(view.scenario?.id);
	const groups = useMemo(() => {
		const out: { label: string; items: Result[] }[] = [];
		const byId = (id: string) => nodes[id];
		if (!query) {
			const figures = (id: string) => home.data?.scenarios.find((c) => c.id === id) ?? null;
			const card = (id: string) => home.data?.nodes.find((c) => c.handle === id)?.headline ?? null;
			const all = Object.values(nodes).sort((a, b) => a.title.localeCompare(b.title));
			if (scenarios.length) out.push({ label: "Scenarios", items: scenarios.map((sc) => ({ kind: "scenario", scenario: sc, figures: figures(sc.id) })) });
			const ev = all.filter((n) => n.kind === "event").map((n): Result => ({ kind: "node", node: n, value: card(n.id) }));
			const me = all.filter((n) => n.kind === "metric").map((n): Result => ({ kind: "node", node: n, value: card(n.id) }));
			if (ev.length) out.push({ label: "Events", items: ev });
			if (me.length) out.push({ label: "Metrics", items: me });
			return out;
		}
		const g = search.data?.groups;
		if (g) {
			const sc = g.scenarios.flatMap((h): Result[] => {
				const scenario = scenarios.find((x) => x.id === h.id);
				return scenario ? [{ kind: "scenario", scenario, figures: { probability: h.probability, hypothetical: h.hypothetical, impossible: false } }] : [];
			});
			const nodeHits = (hits: typeof g.events) =>
				hits.flatMap((h): Result[] => (byId(h.handle) ? [{ kind: "node", node: byId(h.handle), value: h.headline }] : []));
			if (sc.length) out.push({ label: "Scenarios", items: sc });
			const ev = nodeHits(g.events);
			const me = nodeHits(g.metrics);
			if (ev.length) out.push({ label: "Events", items: ev });
			if (me.length) out.push({ label: "Metrics", items: me });
		}
		out.push({
			label: "Create",
			items: [
				{ kind: "create", what: "event" },
				{ kind: "create", what: "metric" },
			],
		});
		return out;
	}, [nodes, scenarios, query, search.data, home.data]);
	const flat = groups.flatMap((g) => g.items);
	const matches = flat.filter((r) => r.kind !== "create").length;

	useEffect(() => {
		listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
	}, [active]);

	function choose(r: Result | undefined) {
		if (!r) return;
		setOpen(false);
		inputRef.current?.blur();
		if (r.kind === "node") navigate(`/node/${r.node.id}`);
		else if (r.kind === "scenario") navigate(`/scenario/${r.scenario.id}`);
		else setCreating({ kind: r.what, title: q.trim() });
		setQ("");
	}

	function value(n: ModelNode, h: HeadlineValueDto | null): string | null {
		if (h) return h.kind === "event" ? `${pct(h.chance)} by ${fmtByDate(h.date)}` : `${fmtValue(h.likely, (n as MetricNode).unit)} by ${fmtByDate(h.date)}`;
		if (!view.data) return null;
		const d = heroDate(n);
		const v = headline(n, view.data, d);
		if (v === null) return null;
		return n.kind === "event" ? `${pct(v)} by ${fmtByDate(d)}` : `${fmtValue(v, (n as MetricNode).unit)} by ${fmtByDate(d)}`;
	}

	let i = -1;
	return (
		<div ref={boxRef} className="relative w-full max-w-2xl">
			<div className={cn("flex h-10 items-center gap-2 rounded-lg border bg-card px-3 shadow-xs transition-shadow", open && "ring-2 ring-ring/40")}>
				<Search className="size-4 shrink-0 text-muted-foreground" />
				<input
					ref={inputRef}
					value={q}
					onChange={(e) => {
						setQ(e.target.value);
						setActive(0);
						setOpen(true);
					}}
					onFocus={() => setOpen(true)}
					onKeyDown={(e) => {
						if (e.key === "ArrowDown") {
							e.preventDefault();
							setActive((a) => Math.min(flat.length - 1, a + 1));
						} else if (e.key === "ArrowUp") {
							e.preventDefault();
							setActive((a) => Math.max(0, a - 1));
						} else if (e.key === "Enter") {
							e.preventDefault();
							choose(flat[active]);
						} else if (e.key === "Escape") {
							setOpen(false);
							inputRef.current?.blur();
						}
					}}
					placeholder={scenarios.length ? "Search events, metrics and scenarios…" : "Search events and metrics…"}
					className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
					role="combobox"
					aria-expanded={open}
					aria-controls="mega-search-results"
					aria-activedescendant={open && flat[active] ? `mega-${active}` : undefined}
					aria-label={scenarios.length ? "Search events, metrics and scenarios" : "Search events and metrics"}
				/>
				<kbd className="hidden rounded border bg-background px-1.5 py-0.5 text-[11px] text-foreground/75 sm:block">⌘K</kbd>
			</div>

			{open && (
				<>
					{
						<div
							ref={listRef}
							id="mega-search-results"
							role="listbox"
							className="absolute top-full right-0 left-0 z-50 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border bg-popover p-1.5 shadow-lg"
						>
							{query && matches === 0 && (
								<p className="px-3 py-2 text-sm text-muted-foreground">{search.pending ? "Searching…" : `Nothing called “${q.trim()}” yet.`}</p>
							)}
							{groups.map((g) => (
								<div key={g.label} className="py-1">
									<div className="px-3 pt-1 pb-1 text-xs font-medium text-muted-foreground">
										{g.label}
										{g.label !== "Create" && <span className="ml-1.5 font-normal">{g.items.length}</span>}
										{view.scenario && (g.label === "Events" || g.label === "Metrics") && (
											<span className="ml-1.5 font-normal">
												· numbers in <span className="font-medium text-foreground">{view.scenario.name}</span>
											</span>
										)}
									</div>
									{g.items.map((r) => {
										i += 1;
										const idx = i;
										const on = idx === active;
										const common = {
											"id": `mega-${idx}`,
											"data-idx": idx,
											"role": "option",
											"aria-selected": on,
											"onMouseMove": () => setActive(idx),
											"onClick": () => choose(r),
											"className": cn(
												"flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-left text-sm",
												on && "bg-accent text-accent-foreground",
											),
										};
										if (r.kind === "node") {
											const n = r.node;
											const v = value(n, r.value);
											return (
												<div key={n.id} {...common}>
													<NodeIcon node={n} className="text-muted-foreground" />
													<div className="min-w-0 flex-1">
														<div className="flex items-center gap-1.5">
															<span className="truncate font-medium">{n.title}</span>
															{n.visibility === "private" && <Lock className="size-3 shrink-0 text-muted-foreground" aria-label="Private" />}
															{(n.kind === "event" ? !!n.definition : !!n.formula) && <span className="text-[11px] text-muted-foreground">calculated</span>}
														</div>
														{n.question && n.question !== n.title && <div className="truncate text-xs text-muted-foreground">{n.question}</div>}
													</div>
													{v && <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{v}</span>}
												</div>
											);
										}
										if (r.kind === "scenario") {
											const chance = r.scenario.levers.length ? chanceLabel(r.scenario, r.figures) : null;
											return (
												<div key={r.scenario.id} {...common}>
													<GitBranch className="size-4 shrink-0 text-[var(--scenario)]" />
													<div className="min-w-0 flex-1">
														<div className="truncate font-medium">{r.scenario.name}</div>
														{r.scenario.description && <div className="truncate text-xs text-muted-foreground">{r.scenario.description}</div>}
													</div>
													<span className="shrink-0 text-right text-xs text-muted-foreground">
														{setsText(r.scenario).replace(/^./, (c) => c.toUpperCase())}
														{chance && <span className="block">{chance}</span>}
													</span>
												</div>
											);
										}
										return (
											<div key={r.what} {...common}>
												<Plus className="size-4 shrink-0 text-muted-foreground" />
												<span>
													New {r.what} “<strong>{q.trim()}</strong>”
												</span>
											</div>
										);
									})}
								</div>
							))}
							<div className="flex gap-4 border-t px-3 pt-2 pb-1 text-[11px] text-muted-foreground">
								<span>↑↓ to move</span>
								<span>↵ to open</span>
								<span>Esc to close</span>
							</div>
						</div>
					}
				</>
			)}

			<NewNodeDialog open={!!creating} onOpenChange={(v) => !v && setCreating(null)} kind={creating?.kind ?? "event"} initialTitle={creating?.title} />
		</div>
	);
}
